// ============================================================
// NAV CONFIG — grouped sidebar structure
//
// `icon` values are semantic Icon names (see components/ui/Icon.jsx), rendered
// as lucide SVGs in the sidebar — not emoji/glyphs.
// ============================================================

export const NAV_LABELS = {
  dashboard:   "Dashboard",
  map:         "Map",
  tasks:       "Tasks",
  farms:       "Farms",
  taskReport:      "Deal Report",
  activityReport:  "Activity Report",
  parcelReport:    "Parcel Report",
  realEstateReport: "Real Estate Report",
  dealNaicsReport: "Deal NAICS Report",
  cashFlowReport:  "Cash Flow Projection",
  financialStatements: "Financial Statements",
  deals:       "Deals",
  globalNotes: "Notes",
  notesByTag:  "Notes by Tag",
  mail:        "Mail",
  calendar:    "Calendar",
  contacts:    "Contacts",
  govwins:     "Gov Opportunities",
  users:       "Users",
  documents:   "Documents",
  docMigration: "Document Migration",
  dam:         "Digital Assets",   // --- DAM module (removable) ---
  emailInbox:  "Email Inbox",
  rates:       "Rate Indexes",
  sage:        "Sage Import",
  arcReview:   "ARC Review",     // --- ARC review module (removable) ---
  userSettings: "My Settings",
  settings:    "Settings",
  retailProducts: "Retail Products",   // --- Retail products module (removable) ---
};

// Grouped nav structure for sidebar rendering.
//
// A group with a `label` renders as ONE collapsible row (icon + label + chevron)
// whose items stay HIDDEN until the user opens it — on hover while the rail is
// hover-revealed, on click when the sidebar is pinned open or on mobile — and it
// closes again whenever the sidebar collapses. Nothing persists an open group
// across reloads, so no sub-menu ever auto-shows. On the minimized icon rail a
// group is a single icon (its `icon`), never its items. `id` keys the open state.
export const NAV_GROUPS = [
  {
    items: [
      { id: "dashboard",  label: "Dashboard",       icon: "dashboard" },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: "reports",
    items: [
      { id: "taskReport",       label: "Deal Report",        icon: "taskReport" },
      { id: "activityReport",   label: "Activity Report",    icon: "activityReport" },
      { id: "parcelReport",     label: "Parcel Report",      icon: "parcelReport" },
      { id: "realEstateReport", label: "Real Estate Report", icon: "realEstateReport" },
      // Projected cash flow per deal, from each capital item's budget, capital
      // schedule or valuation, at a resolution the reader picks.
      { id: "cashFlowReport",   label: "Cash Flow Projection", icon: "ledger" },
      // Neil-only: every deal (dead or live) with its NAICS industry and total
      // cost. `ownerEmail` gates it to exactly one user; the API enforces the
      // same guard server-side (see canSeeNav in App.jsx).
      { id: "dealNaicsReport", label: "Deal NAICS Report", icon: "reports", ownerEmail: "ngodfrey@harvus.com" },
      // Income statement + balance sheet from the Sage GL — per entity,
      // consolidated or per deal. Neil and Kurt only; `ownerEmails` mirrors the
      // API's FINANCIAL_STATEMENT_VIEWERS (api/src/restrictedViewers.js).
      { id: "financialStatements", label: "Financial Statements", icon: "ledger",
        ownerEmails: ["ngodfrey@harvus.com", "kschneider@harvus.com"] },
    ],
  },
  {
    items: [
      { id: "deals",      label: "Deals",           icon: "deals" },
      { id: "tasks",      label: "Tasks",           icon: "tasks" },
      { id: "globalNotes", label: "Notes",          icon: "note" },
      // Your own Outlook mail/calendar, through the same Microsoft connection
      // Harvi's mail/calendar tools use (My Settings → Connected Accounts).
      // Nothing deal-scoped — this is the person's own mailbox and calendar.
      { id: "mail",       label: "Mail",            icon: "mail" },
      { id: "calendar",   label: "Calendar",        icon: "calendar" },
      { id: "govwins",    label: "Gov Opportunities", icon: "govwins" },
    ],
  },
  {
    // Shared reference material and the places files and mail land.
    id: "resources",
    label: "Resources",
    icon: "folder",
    items: [
      { id: "map",        label: "Map",             icon: "map" },
      { id: "contacts",   label: "Contacts",        icon: "contacts" },
      { id: "documents",  label: "Documents",       icon: "documents" },
      { id: "emailInbox", label: "Email Inbox",     icon: "emailInbox", adminOnly: true },
      { id: "dam",        label: "Digital Assets",  icon: "image" },   // --- DAM module (removable) ---
      // Neil-only bulk document migration tool. `ownerEmail` gates it to exactly
      // one user (enforced again server-side); see canSeeNav in App.jsx.
      { id: "docMigration", label: "Document Migration", icon: "documents", ownerEmail: "ngodfrey@harvus.com" },
    ],
  },
  {
    // One-off modules with no natural home that only a few people use.
    id: "more",
    label: "More",
    icon: "more",
    items: [
      { id: "farms",      label: "Farms",           icon: "farms" },
      // --- ARC review module (removable) --- Architectural Review Committee:
      // plans arrive at arc@, land on a lot, and are measured against the
      // community's adopted standards. Reads are open to any authenticated user
      // (the committee is not all admins); every write is Admin server-side.
      { id: "arcReview",  label: "ARC Review",      icon: "ruler" },
      // --- Retail products module (removable) --- a wholesale UPC book tracked
      // by vendor, each item's picture looked up from the public barcode
      // databases. Reads are open to any authenticated user; imports, edits and
      // lookups are Admin server-side.
      { id: "retailProducts", label: "Retail Products", icon: "barcode" },
    ],
  },
  {
    // "My Settings" is everyone's own account — notification channels, phone
    // number, quiet hours, and the personal Microsoft connections (mail &
    // calendar, To Do). Deliberately NOT adminOnly: it changes only the
    // signed-in user's own row. Admin is consolidated into a single Settings
    // entry below it: Users, Sage Import, Rate Indexes, Payment Generation,
    // Notifications and Connection Tests are all rendered as tabs inside the
    // Settings page.
    items: [
      { id: "userSettings", label: "My Settings", icon: "user" },
      { id: "settings",     label: "Settings",     icon: "settings", adminOnly: true },
    ],
  },
];
